/**
 * CallSuccessChart Component
 * 
 * Donut chart displaying call transfer rate as a percentage
 */

import React from 'react';
import PropTypes from 'prop-types';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

/**
 * @param {Object} props
 * @param {Object} props.data - Object with {transferred, not_transferred, transfer_rate} counts and percentage
 * @param {boolean} [props.isLoading] - Loading state
 */
const CallSuccessChart = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <div className="mb-6">
          <h3 className="text-base font-semibold text-white">Call Transfer Rate</h3>
          <p className="text-sm text-gray-400">All agents</p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3AD0E6]" />
        </div>
      </div>
    );
  }

  if (!data || (data.transferred === 0 && data.not_transferred === 0)) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <div className="mb-6">
          <h3 className="text-base font-semibold text-white">Call Transfer Rate</h3>
          <p className="text-sm text-gray-400">All agents</p>
        </div>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-400">No data available</p>
        </div>
      </div>
    );
  }

  // Prepare data for pie chart
  const chartData = [
    { name: 'Not Transferred', value: data.not_transferred || 0, color: '#3AD0E6' },
    { name: 'Transferred', value: data.transferred || 0, color: '#3630A4' }
  ];

  const transferRate = data.transfer_rate || 0;

  // Custom label renderer
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null; // Don't show label if too small

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ fontSize: '14px', fontWeight: '600' }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
      <div className="mb-6">
        <h3 className="text-base font-semibold text-white">Call Transfer Rate</h3>
        <p className="text-sm text-gray-400">All agents</p>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={80}
            outerRadius={120}
            paddingAngle={2}
            dataKey="value"
            label={renderCustomLabel}
            labelLine={false}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: '#1f2937',
              border: '1px solid #374151',
              borderRadius: '8px',
              padding: '8px 12px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
              color: '#ffffff'
            }}
            labelStyle={{ color: '#ffffff', fontWeight: '600' }}
            itemStyle={{ color: '#ffffff' }}
            formatter={(value) => [value, 'Calls']}
          />
        </PieChart>
      </ResponsiveContainer>

      <div className="mt-4 flex items-center justify-center gap-6">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#3AD0E6]"></div>
          <span className="text-xs text-gray-400">Not Transferred</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#3630A4]"></div>
          <span className="text-xs text-gray-400">Transferred</span>
        </div>
      </div>

      {/* Transfer Rate Percentage */}
      <div className="mt-4 text-center">
        <p className="text-2xl font-semibold text-white">{transferRate.toFixed(2)}%</p>
        <p className="text-xs text-gray-400">Transfer Rate</p>
      </div>
    </div>
  );
};

CallSuccessChart.propTypes = {
  data: PropTypes.shape({
    transferred: PropTypes.number,
    not_transferred: PropTypes.number,
    transfer_rate: PropTypes.number
  }),
  isLoading: PropTypes.bool
};

export default CallSuccessChart;

